import React, { useRef, useState } from 'react';
import TabNavigation, { TabConfig } from '../../Components/common/TabNavigation';
import ArrowUpIcon from '../../Images/arrow-up-1.svg';
import DownloadIcon from '../../Images/download-icon.svg';
import CloseIcon from '../../Images/CloseIcon.svg';
import { uploadMasterVariables, downloadMasterVariables } from '../../Services/serviceURL';
import './Securities.css';

const Securities: React.FC = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    
    const tabConfig: TabConfig[] = [
        { key: 'Plants', label: 'Plants', route: '/plants' },
        { key: 'Profile', label: 'Users', route: '/users' },
        { key: 'Securities', label: 'Securities', route: '/securities' }
    ];

    const handleUpload = () => {
        setIsUploadModalOpen(true);
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        setSelectedFile(file || null);
    };

    const handleBrowseFiles = () => {
        fileInputRef.current?.click();
    };

    const handleUploadFile = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(null);

        try {
            const response = await uploadMasterVariables(selectedFile);
            setUploadSuccess('File uploaded successfully!');
            console.log('Upload response:', response);
            
            setTimeout(() => {
                handleCloseModal();
            }, 2000);
        } catch (error: any) {
            console.error('Upload error:', error);
            setUploadError(
                error.response?.data?.message || 
                error.message || 
                'Failed to upload file. Please try again.'
            );
        } finally {
            setIsUploading(false);
        }
    };

    const handleCloseModal = () => {
        setIsUploadModalOpen(false);
        setSelectedFile(null);
        setUploadError(null);
        setUploadSuccess(null);
        setIsUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const response = await downloadMasterVariables();

            // Extract filename from response headers or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'master_variables.csv';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?(.*)['"]?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = decodeURIComponent(filenameMatch[1].replace(/\+/g, '%20'));
                }
            }

            // Create blob and download
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();

            console.log('Download completed successfully');
        } catch (error: any) {
            console.error('Download error:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="securities-container">
            <TabNavigation tabs={tabConfig} className="tabs-plants" />
            
            <div className="securities-actions">
                <button className="upload-button" onClick={handleUpload}>
                    <img src={ArrowUpIcon} alt="upload" className="button-icon" />
                    Upload
                </button>
                <button className="download-button" onClick={handleDownload} disabled={isDownloading}>
                    <img src={DownloadIcon} alt="download" className="button-icon" />
                    {isDownloading ? 'Downloading...' : 'Download'}
                </button>
            </div>

            {/* Upload Modal */}
            {isUploadModalOpen && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Upload File</h3>
                            <button className="close-button" onClick={handleCloseModal}>
                                <img src={CloseIcon} alt="close" className="close-icon" />
                            </button>
                        </div>

                        {isUploading && (
                            <div className="modal-uploading-overlay">
                                <span className="loading-spinner modal-loading-spinner"></span>
                                <p className="uploading-title">Uploading...</p>
                            </div>
                        )}
                        
                        <div className="modal-content">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                accept=".csv"
                                style={{ display: 'none' }}
                            />
                            
                            {uploadError && (
                                <div className="upload-message error-message">
                                    {uploadError}
                                </div>
                            )}
                            
                            {uploadSuccess && (
                                <div className="upload-message success-message">
                                    {uploadSuccess}
                                </div>
                            )}
                            
                            <div className="file-drop-zone" onClick={!isUploading ? handleBrowseFiles : undefined}>
                                {selectedFile ? (
                                    <div className="file-info">
                                        <div className="file-icon">📄</div>
                                        <div className="file-name">{selectedFile.name}</div>
                                        <div className="file-size">{(selectedFile.size / 1024).toFixed(2)} KB</div>
                                    </div>
                                ) : (
                                    <div className="drop-zone-content">
                                        <div className="upload-icon">📁</div>
                                        <p>Click to browse files</p>
                                        <p className="file-hint">Only CSV files are allowed</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="modal-actions">
                            <button 
                                className="cancel-button" 
                                onClick={handleCloseModal}
                                disabled={isUploading}
                            >
                                Cancel
                            </button>
                            <button 
                                className="upload-confirm-button" 
                                onClick={handleUploadFile}
                                disabled={!selectedFile || isUploading}
                            >
                                Upload
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Securities;